// Shared filter logic + persisted-state keys for the Deliveries list.
// Used by Deliveries.tsx (the live board) AND DeliveriesReport.tsx (the
// print-friendly report). Both pages read from the same localStorage
// keys so the operator's current filters carry into the report without
// having to re-type them.

import type { Delivery } from '@/types/database'
import { supabase } from '@/lib/supabase'
import { fetchAllPaged } from '@/lib/fetchAllPaged'

// Shared deliveries fetch for the board + the print report. Drains the set in
// bounded pages (never a 50k cap-lift). `serverFromIso` scopes the server window
// (the board passes its 90-day / All-history cutoff; the report passes null to
// pull all-matching). Client-side filterDeliveries() then applies the operator
// filters. One fetch shared by both pages, so neither carries its own cap-lift.
export async function fetchAllDeliveries(
  serverFromIso: string | null,
  db: typeof supabase = supabase,   // pass an isolated client (useSaveDb) to keep this read off the busy board's auth lock
): Promise<Delivery[]> {
  const { rows, error } = await fetchAllPaged<Delivery>((lo, hi) => {
    let q = db
      .from('deliveries')
      .select('*, delivery_additions(addition_id, bins, source, added_at, delivery_note_number)')
      .is('deleted_at', null)
      .order('received_at', { ascending: false })
      .range(lo, hi)
    if (serverFromIso) q = q.gte('received_at', serverFromIso)
    return q as unknown as PromiseLike<{ data: Delivery[] | null; error: { message: string } | null }>
  })
  if (error) throw new Error(error)
  return rows
}

export type SearchField =
  | 'any' | 'grower' | 'variety' | 'puc' | 'block' | 'delivery_point'
  | 'delivery_note_number' | 'client_ref_no' | 'vehicle_reg' | 'driver_name'

// Fields swept when the operator picks "Any field". Listed explicitly so a
// future column on Delivery doesn't silently start matching searches.
export const ANY_FIELD_KEYS: SearchField[] = [
  'grower', 'variety', 'puc', 'block', 'delivery_point',
  'delivery_note_number', 'client_ref_no', 'vehicle_reg', 'driver_name',
]

// The "no point recorded" bucket for the delivery-point facet. A sentinel
// rather than '' because '' already means "don't filter at all" — every legacy
// delivery and all citrus intake have a NULL point, so operators need to be
// able to select them explicitly.
export const UNSPECIFIED_DELIVERY_POINT = '__none__'

// LocalStorage keys for the persisted filters. Both Deliveries page and
// the print report use these via usePersistedState, so changing a filter
// on the board carries straight into the report.
export const FILTER_STORAGE_KEYS = {
  statusFilter:  'kf49.deliveries.statusFilter',
  growerFilter:  'kf49.deliveries.growerFilter',
  varietyFilter: 'kf49.deliveries.varietyFilter',
  blockFilter:   'kf49.deliveries.blockFilter',
  deliveryPointFilter: 'kf49.deliveries.deliveryPointFilter',
  fromFilter:    'kf49.deliveries.fromFilter',
  toFilter:      'kf49.deliveries.toFilter',
  searchField:   'kf49.deliveries.searchField',
  searchQuery:   'kf49.deliveries.searchQuery',
} as const

export interface DeliveriesFilter {
  /** 'ALL' | DeliveryStatus | 'PALTRACK_PENDING' | 'NEEDS_CHECK' */
  statusFilter:  string
  growerFilter:  string
  varietyFilter: string
  blockFilter:   string
  /**
   * Point NAME, or UNSPECIFIED_DELIVERY_POINT for "no point recorded".
   * OPTIONAL so existing call sites are untouched — a page that doesn't offer
   * the facet simply never filters on it (same posture as the nullable column).
   */
  deliveryPointFilter?: string
  fromFilter:    string   // YYYY-MM-DD
  toFilter:      string   // YYYY-MM-DD
  searchField:   SearchField
  searchQuery:   string
  /** delivery_id -> newest tip time; needed only by the NEEDS_CHECK status. */
  latestTipByDelivery?: Map<string, string>
}

/**
 * Does this delivery's point match the operator's facet selection?
 *
 * Extracted from the filter predicate so the three-way rule (no filter / the
 * "unspecified" sentinel / an exact name) is directly testable and doesn't
 * push the already-dense predicate further over the complexity gate.
 *
 * Matches on the NAME SNAPSHOT (deliveries.delivery_point), which the DB
 * stamps — not the id — so the facet keeps working for a delivery whose point
 * was later deactivated or renamed.
 */
export function matchesDeliveryPoint(
  deliveryPoint: string | null | undefined,
  filter: string | undefined,
): boolean {
  if (!filter) return true                       // facet not in use
  const point = (deliveryPoint ?? '').trim()
  // The sentinel selects rows with NO point — the legacy/citrus majority.
  if (filter === UNSPECIFIED_DELIVERY_POINT) return point === ''
  return point === filter
}

/**
 * UF-07: a delivery nobody has signed off. Same rule as the card badges:
 * the delivery check is missing or older than the newest load; or the delivery
 * has tips and the tipping check is missing or older than the newest tip.
 * A delivery with no tips needs no tipping check.
 */
export function needsCheck(d: Delivery, latestTipAt: string | null): boolean {
  if (d.status === 'DRAFT') return false
  const row = d as Delivery & { admin_checked_at?: string | null; tipping_checked_at?: string | null }
  const loads = d.delivery_additions ?? []
  const latestLoadAt = loads.reduce<string | null>((m, l) => (m == null || l.added_at > m ? l.added_at : m), null)
  const adminChecked = row.admin_checked_at
  if (!adminChecked || (latestLoadAt != null && latestLoadAt > adminChecked)) return true
  if (latestTipAt == null) return false
  const tipChecked = row.tipping_checked_at
  return !tipChecked || latestTipAt > tipChecked
}

/**
 * Status rule. DRAFT is never listed (it isn't a received delivery yet), and
 * PALTRACK_PENDING is a pseudo-status over a separate column rather than a
 * `status` value — which is why this isn't a plain equality test.
 */
function matchesStatus(d: Delivery, statusFilter: string, latestTip?: Map<string, string>): boolean {
  if (d.status === 'DRAFT') return false
  if (statusFilter === 'NEEDS_CHECK') return needsCheck(d, latestTip?.get(d.delivery_id) ?? null)
  if (statusFilter === 'PALTRACK_PENDING') {
    return (d as Delivery & { paltrack_status?: string }).paltrack_status === 'pending'
  }
  return statusFilter === 'ALL' || d.status === statusFilter
}

/**
 * UF-04: the one line under the chips. `total` counts every non-draft
 * delivery the board has loaded (drafts are announcements, never rows).
 * Parts after the count name what narrows the view: the status chip (when not
 * All) and the history window.
 */
export function boardSummary(
  deliveries: Delivery[], shown: number, statusLabel: string | null, showAllHistory: boolean,
): string {
  const total = deliveries.filter(d => d.status !== 'DRAFT').length
  return [
    `Showing ${shown} of ${total} deliveries`,
    ...(statusLabel ? [statusLabel] : []),
    showAllHistory ? 'All history' : 'Last 90 days',
  ].join(' · ')
}

/**
 * Free-text search across either one chosen field or the ANY_FIELD_KEYS sweep.
 * `searchTerm` must already be trimmed + lower-cased (hoisted out of the loop
 * by the caller so it isn't recomputed per row).
 */
function matchesSearch(d: Delivery, searchField: SearchField, searchTerm: string): boolean {
  if (!searchTerm) return true
  const keys = searchField === 'any' ? ANY_FIELD_KEYS : [searchField]
  const row = d as Delivery & Record<string, string | null | undefined>
  return keys.some(k => {
    const v = row[k]
    return v != null && String(v).toLowerCase().includes(searchTerm)
  })
}

// Inclusive date window: `to` compares against the start of the NEXT day
// so a delivery received at 23:50 still falls under that calendar day.
// SAST-anchored (UTC+2) — the gate operator's clock.
//
// The per-field rules live in the small helpers above so this stays a readable
// list of AND-ed conditions rather than one dense predicate.
export function filterDeliveries(deliveries: Delivery[], f: DeliveriesFilter): Delivery[] {
  const fromTs = f.fromFilter ? new Date(`${f.fromFilter}T00:00:00+02:00`).getTime() : null
  const toTs   = f.toFilter   ? new Date(`${f.toFilter}T00:00:00+02:00`).getTime() + 86_400_000 : null
  const searchTerm = f.searchQuery.trim().toLowerCase()

  // Trips CRAP only (cyclomatic + cognitive are both inside the gate after the
  // status / search / point rules moved to the helpers above). What remains is
  // an irreducible list of AND-ed field checks — one line per filter, 11 lines
  // total. CRAP is computed from an ESTIMATED coverage figure here; every branch
  // below is in fact covered by deliveriesFilter.test.ts, so decomposing further
  // would only scatter the rules without changing the number.
  // fallow-ignore-next-line complexity
  return deliveries.filter(d => {
    if (!matchesStatus(d, f.statusFilter, f.latestTipByDelivery)) return false
    if (f.growerFilter  && d.grower.trim()  !== f.growerFilter)  return false
    if (f.varietyFilter && d.variety.trim() !== f.varietyFilter) return false
    if (f.blockFilter   && (d.block ?? '').trim() !== f.blockFilter) return false
    if (!matchesDeliveryPoint(d.delivery_point, f.deliveryPointFilter)) return false
    const ts = new Date(d.received_at).getTime()
    if (fromTs !== null && ts < fromTs) return false
    if (toTs   !== null && ts >= toTs)  return false
    return matchesSearch(d, f.searchField, searchTerm)
  })
}
